import style from './Styles/index.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faExclamationCircle,
    faQuestion,
} from '@fortawesome/free-solid-svg-icons'
import React, { memo, useRef, useState } from 'react'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import DOMPurify from 'dompurify'
import { NoData } from '../../NoData'
import { Placement } from 'react-bootstrap/types'
import { descriptionWindowActions } from '../../../store/slices/descriptionWindowSlice'
import { withTooltip } from '../../../utils/withTooltip'

interface DescriptionBtnProps {
    type: 'validation' | 'small' | 'default'
    header: string
    description: string
    className?: string
    placement?: Placement
}

export const DescriptionBtnWithPopup = memo(function DescriptionBtnWithPopup(
    props: DescriptionBtnProps
) {
    const { type, header, description, className, placement = 'top' } = props

    const dispatch = useAppDispatch()
    const [show, setShow] = useState(false)
    const hoverTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

    const isString = typeof description === 'string'
    const hasHrTag = isString && description.includes('<hr>')
    const shortDescription = hasHrTag
        ? description.split('<hr>')[0]
        : description
    const sanitizedHTML = isString
        ? DOMPurify.sanitize(shortDescription, {
              USE_PROFILES: { html: true },
          })
        : ''

    const handleOnMouseEnter = () => {
        clearTimeout(hoverTimeoutRef.current)
        setShow(true)
    }
    const handleOnMouseLeave = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            setShow(false)
        }, 1000)
    }
    const handleOnPopoverMouseEnter = () => {
        clearTimeout(hoverTimeoutRef.current)
        setShow(true)
    }

    const handleClick = () => {
        dispatch(
            descriptionWindowActions.setDescriptionData({ header, description })
        )
        dispatch(descriptionWindowActions.show())
        setShow(false)
    }

    const descriptionOverlay = (
        <Popover
            onMouseEnter={handleOnPopoverMouseEnter}
            onMouseLeave={handleOnMouseLeave}
            id={`description-overlay-top`}
        >
            <Popover.Body>
                {description && sanitizedHTML ? (
                    <div dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />
                ) : description ? (
                    description
                ) : (
                    <NoData style={{ height: '50%' }} />
                )}
                {hasHrTag && (
                    <div
                        onClick={handleClick}
                        className={`${style.readMore} d-inline-block mt-2`}
                    >
                        <FormattedMessage id={'buttons.readMore'} />
                    </div>
                )}
            </Popover.Body>
        </Popover>
    )

    switch (type) {
        case 'small':
            return (
                <OverlayTrigger
                    overlay={descriptionOverlay}
                    show={show}
                    placement={placement}
                >
                    <span
                        onMouseEnter={handleOnMouseEnter}
                        onMouseLeave={handleOnMouseLeave}
                        className={`${style.smallQuestion} ${className}`}
                    >
                        <FontAwesomeIcon icon={faQuestion} />
                    </span>
                </OverlayTrigger>
            )

        case 'validation':
            return withTooltip(
                <span
                    onClick={handleClick}
                    className={[
                        style.smallButton,
                        'ms-2 pointer text-danger border-danger border',
                        className,
                    ].join(' ')}
                >
                    <FontAwesomeIcon icon={faExclamationCircle} />
                </span>,
                'Description',
                'descriptionBtn'
            )

        default:
            return (
                <OverlayTrigger
                    overlay={descriptionOverlay}
                    show={show}
                    placement={placement}
                >
                    <span
                        onMouseEnter={handleOnMouseEnter}
                        onMouseLeave={handleOnMouseLeave}
                        className={`${style.button} ms-2 ${className}`}
                    >
                        <FontAwesomeIcon icon={faQuestion} />
                    </span>
                </OverlayTrigger>
            )
    }
})
